import { Select } from '@helix/select'
import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { FontOption } from './useFontOptions'

interface FontSelectProps {
  value: string
  options: FontOption[]
  onChange: (value: string) => void
  onBlur?: () => void
  isDisabled?: boolean
  hasError?: boolean
  'aria-label'?: string
}

const findFontOption = (options: FontOption[], value: string) => {
  const normalized = value.trim().toLowerCase()
  return options.find(
    (font) => font.key === value || font.value.toLowerCase() === normalized
  )
}

export function FontSelect({
  value,
  options,
  onChange,
  onBlur,
  isDisabled,
  hasError,
  'aria-label': ariaLabel
}: FontSelectProps) {
  const selected = useMemo(() => {
    if (!value) return null
    return findFontOption(options, value) || { key: value, value }
  }, [options, value])

  return (
    <>
      <Helmet>
        {options.map(
          (font) =>
            font.url && <link key={font.key} rel="stylesheet" href={font.url} />
        )}
      </Helmet>
      <Select
        aria-label={ariaLabel}
        placeholder="Select a font"
        options={options}
        value={selected}
        getOptionValue={(font: FontOption) => font.key}
        getOptionLabel={(font: FontOption) => font.value}
        formatOptionLabel={(font: FontOption) => (
          <span style={{ fontFamily: `"${font.value}", sans-serif` }}>
            {font.value}
          </span>
        )}
        onChange={(font: FontOption | null) => onChange(font?.key || '')}
        onBlur={onBlur}
        isDisabled={isDisabled}
        isClearable={false}
        validationType={hasError ? 'invalid' : undefined}
      />
    </>
  )
}
